import React, { useState, useMemo, useCallback } from "react";
import { normalizeTurnMode } from "@/lib/normalize-turn-mode";
import { useUserRole } from "@/hooks/useUserRole";
import { storyElement } from "@/lib/story-element";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent } from "@/components/ui/card";
import {
  User, MapPin, Users, Clock, Shield,
  Crown, UserCircle, Copy, Check, Compass, Clapperboard, ClipboardList,
  ChevronsLeft, ChevronsRight, BookOpen,
} from "lucide-react";
import { DirectorDebugPanel } from "./DirectorDebugPanel";
import { tFormat } from "@/lib/i18n/t-format";
import { Button } from "@/components/ui/button";
import { CharacterSheet } from "./CharacterSheet";
import { PartyPanel } from "./PartyPanel";
import { CharacterThoughts } from "./CharacterThoughts";
import { PlayerCommitments } from "./PlayerCommitments";
import { MultiplayerPanel } from "./MultiplayerPanel";
import { PresenceAvatars } from "@/components/presence/PresenceAvatars";
import { toast } from "sonner";
import { JournalPanel, type JournalEntry } from "./JournalPanel";
import { LoreCodexPanel } from "./LoreCodexPanel";
import { QuestJournal } from "./QuestJournal";
import { PlayerNotesPanel } from "./PlayerNotesPanel";
import { buildWidgetLayout, resolveWidgetData } from "@/lib/mechanics/registry";
import type { MechanicConfig, ResolvedWidget } from "@/lib/mechanics/registry";
import { renderWidget } from "./WidgetRegistry";
import * as LucideIcons from "lucide-react";
import { useLocale } from "@/hooks/useLocale";

interface GameHUDProps {
  adventure: any;
  players: any[];
  currentUserId: string | null;
  isHost: boolean;
  activeMechanics: MechanicConfig[];
  variables: Record<string, any>;
  state: any;
  playerChar: any;
  adventureId?: string;
  myPlayer: any;
  otherUsers: any[];
  combatants?: any[];
  onCombatAction?: (action: string) => void;
  isSending?: boolean;
  canSend?: boolean;
  allowMediaGeneration?: boolean;
  onWidgetAction?: (action: any, params: Record<string, string>) => void;
  mobile?: boolean;
  characterThought?: string | null;
  isGeneratingThought?: boolean;
  entityImageMap?: Record<string, string>;
  onJournalEntriesChange?: (entries: JournalEntry[]) => void;
}

/* ─── Helpers ─── */

function getIcon(iconName: string): React.ComponentType<any> {
  return (LucideIcons as any)[iconName] || Compass;
}

function PlayerList({ players, currentUserId, currentPlayerId, turnMode, t }: any) {
  return (
    <div className="space-y-1">
      {players.map((p: any) => {
        const isMe = p.user_id === currentUserId;
        const isTurn = normalizeTurnMode(turnMode) === "round_robin" && p.user_id === currentPlayerId;
        return (
          <div key={p.id} className={cn(
            "flex items-center gap-2 text-xs p-2 rounded-lg transition-colors",
            isTurn ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/30",
          )}>
            {p.is_host
              ? <Crown className="w-3.5 h-3.5 text-primary shrink-0" />
              : <UserCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
            <span className={cn("truncate", isMe ? "font-medium text-foreground" : "text-muted-foreground")}>
              {p.character_name || p.profile?.full_name || t.play_anonymous}{isMe && ` ${t.play_you_suffix}`}
            </span>
            {isTurn && <Badge variant="default" className="text-[10px] h-4 px-1 ml-auto shrink-0">{t.play_turn_label}</Badge>}
          </div>
        );
      })}
    </div>
  );
}

function InviteCodeCard({ inviteCode, t }: { inviteCode: string; t: any }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${inviteCode}`);
    setCopied(true);
    toast.success(t.play_invite_copied);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border">
      <code className="text-xs font-mono bg-muted px-2 py-1 rounded flex-1 text-center">{inviteCode}</code>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy} aria-label={copied ? "Copied!" : "Copy invite code"}>
        {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
      </Button>
    </div>
  );
}

/* ─── Status Strip ─── */
export function StatusStrip({ state, variables, statusItems, mobile }: { state: any; variables: any; statusItems: ResolvedWidget[]; mobile?: boolean }) {
  const { t } = useLocale();
  const [locationOpen, setLocationOpen] = useState(false);
  const [turnOpen, setTurnOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const inCombat = (state?.context_tags || []).includes("combat");
  const hour = variables?.game_hour ?? null;
  const day = variables?.game_day ?? null;
  const weather = variables?.current_weather;
  const timeStr = hour !== null ? `${String(hour).padStart(2, "0")}:00` : null;
  const phase = hour !== null ? (hour < 6 ? "🌙" : hour < 12 ? "🌅" : hour < 18 ? "☀️" : "🌆") : null;
  const turn = state?.turn_count || 0;
  const location = state?.current_location || "Unknown";
  const pcName = state?.variables?.player_name || variables?.player_name || "Hero";
  const activeNpcs: string[] = state?.active_npcs || [];

  const extraCells: { label: string; value: React.ReactNode }[] = [];
  statusItems.forEach((item) => {
    const widget = renderWidget(item, { compact: true });
    if (widget) extraCells.push({ label: item.widget.label || item.widget.id, value: widget });
  });

  const pillClass = "flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full cursor-pointer transition-colors hover:bg-accent/60 border border-border/40 bg-card/60 select-none";
  const activePillClass = "bg-accent border-accent";

  return (
    <div {...storyElement("status-strip")} className="border-b border-border/50 bg-muted/10">
      <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5">

        {/* ── Location popover ── */}
        <Popover open={locationOpen} onOpenChange={setLocationOpen}>
          <PopoverTrigger asChild>
            <button className={cn(pillClass, locationOpen && activePillClass)}>
              <MapPin className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-primary shrink-0" />
              <span className="text-[10px] sm:text-xs font-medium text-foreground truncate max-w-[90px] sm:max-w-[180px]">{location}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="bottom" sideOffset={6} className="w-72 p-0">
            <Card className="border-0 shadow-none">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">{location}</span>
                </div>
                {weather && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <LucideIcons.Cloud className="w-3.5 h-3.5" />
                    <span>{weather}</span>
                  </div>
                )}
                {activeNpcs.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.play_present}</div>
                    <div className="flex flex-wrap gap-1">
                      {activeNpcs.map((npc) => (
                        <Badge key={npc} variant="secondary" className="text-[11px] h-5">{npc}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {state?.context_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {state.context_tags.map((tag: string) => (
                      <Badge key={tag} variant="outline" className="text-[10px] h-4 px-1.5">{tag}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </PopoverContent>
        </Popover>

        {/* ── Turn popover ── */}
        <Popover open={turnOpen} onOpenChange={setTurnOpen}>
          <PopoverTrigger asChild>
            <button className={cn(pillClass, turnOpen && activePillClass)}>
              <Clock className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-muted-foreground shrink-0" />
              <span className="text-[10px] sm:text-xs font-medium text-foreground tabular-nums">T{turn}</span>
              {inCombat && <LucideIcons.Swords className="w-2.5 sm:w-3 h-2.5 sm:h-3 text-destructive animate-pulse" />}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="bottom" sideOffset={6} className="w-72 p-0">
            <Card className="border-0 shadow-none">
              <CardContent className="p-4 space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{t.play_turn_label}</div>
                    <div className="text-lg font-bold tabular-nums text-foreground">{turn}</div>
                  </div>
                  {day != null && (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{t.play_day}</div>
                      <div className="text-lg font-bold tabular-nums text-foreground">{day}</div>
                    </div>
                  )}
                  {timeStr && (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{t.play_time}</div>
                      <div className="text-sm font-medium text-foreground">{phase} {timeStr}</div>
                    </div>
                  )}
                  {inCombat && (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{t.play_mode}</div>
                      <Badge variant="destructive" className="text-[11px] h-5 gap-1 animate-pulse">
                        <LucideIcons.Swords className="w-3 h-3" />{t.play_combat}
                      </Badge>
                    </div>
                  )}
                </div>
                {variables?._current_phase && (
                  <div className="border-t border-border/30 pt-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t.play_phase}</div>
                    <div className="flex gap-1">
                      {["action", "resolution", "narration"].map((p) => (
                        <Badge key={p} variant={variables._current_phase === p ? "default" : "outline"} className="text-[10px] h-5 capitalize">
                          {p}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {Array.isArray(variables?.turn_order) && variables.turn_order.length > 0 && (
                  <div className="border-t border-border/30 pt-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t.play_initiative_order}</div>
                    <div className="space-y-0.5">
                      {variables.turn_order.map((entry: any, i: number) => (
                        <div key={i} className={cn(
                          "flex items-center justify-between text-xs px-2 py-1 rounded",
                          entry.name === variables?.current_turn_player ? "bg-primary/15 text-primary font-medium" : "text-muted-foreground",
                        )}>
                          <span className="flex items-center gap-1.5">
                            <span className="text-[10px] tabular-nums w-4 text-right">{i + 1}.</span>
                            {entry.name}
                            {entry.name === variables?.current_turn_player && <span className="text-[10px]">◀</span>}
                          </span>
                          <span className="text-[10px] tabular-nums">{entry.initiative}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {extraCells.length > 0 && (
                  <div className="border-t border-border/30 pt-2 grid grid-cols-2 gap-2">
                    {extraCells.map((cell, i) => (
                      <div key={i}>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{cell.label}</div>
                        <div className="text-xs font-medium text-foreground">{cell.value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </PopoverContent>
        </Popover>

        {(variables?.player_hp != null) && (
          <div className={cn(pillClass, "cursor-default")}>
            <LucideIcons.Heart className={cn("w-3 sm:w-3.5 h-3 sm:h-3.5 shrink-0", (variables.player_hp / (variables.player_max_hp || 20)) > 0.5 ? "text-green-500" : (variables.player_hp / (variables.player_max_hp || 20)) > 0.25 ? "text-primary" : "text-destructive")} />
            <span className="text-[10px] sm:text-xs font-medium text-foreground tabular-nums">{variables.player_hp}/{variables.player_max_hp || 20}</span>
          </div>
        )}

        {/* ── Avatar / Character popover ── */}
        <Popover open={avatarOpen} onOpenChange={setAvatarOpen}>
          <PopoverTrigger asChild>
            <button className={cn(pillClass, avatarOpen && activePillClass, "ml-auto")}>
              <div className="w-4 sm:w-5 h-4 sm:h-5 rounded-full bg-primary/20 flex items-center justify-center text-[8px] sm:text-[10px] font-bold text-primary shrink-0">
                {pcName.charAt(0).toUpperCase()}
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-foreground truncate max-w-[60px] sm:max-w-[120px]">{pcName}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" side="bottom" sideOffset={6} className="w-72 p-0">
            <Card className="border-0 shadow-none">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                    {pcName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                  <div className="text-sm font-semibold text-foreground">{pcName}</div>
                  <div className="text-[10px] text-muted-foreground">{t.play_player_character}</div>
                </div>
              </div>
              {state?.variables?.hp != null && (
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.play_hp}</div>
                    <div className="text-xs font-medium text-foreground">{state.variables.hp}{state.variables.max_hp ? ` / ${state.variables.max_hp}` : ""}</div>
                  </div>
                  {state.variables.level != null && (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.play_level}</div>
                      <div className="text-xs font-medium text-foreground">{state.variables.level}</div>
                    </div>
                  )}
                  {state.variables.gold != null && (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.play_gold}</div>
                      <div className="text-xs font-medium text-foreground">{state.variables.gold}</div>
                    </div>
                  )}
                </div>
              )}
              </CardContent>
            </Card>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

/* ─── World/Scene Tab ─── */
function WorldTab({ state, variables, adventure, players, currentUserId, isHost, t, adventureId, activeMechanics, onWidgetAction }: any) {
  // Gather known NPCs and items for the codex
  const knownNpcs = (state?.active_npcs || []).map((name: string) => ({ name }));
  const inventoryItems = (variables?.player_inventory_detailed || variables?.inventory || []).map((item: any) =>
    typeof item === "string" ? { name: item } : item
  );

  return (
    <div className="space-y-4">
      {/* Scene context */}
      {state?.active_npcs?.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">{t.play_present}</div>
          <div className="flex flex-wrap gap-1.5">
            {state.active_npcs.map((npc: string) => (
              <Badge key={npc} variant="secondary" className="text-xs">{npc}</Badge>
            ))}
          </div>
        </div>
      )}
      {state?.context_tags?.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">{t.play_scene_tags}</div>
          <div className="flex flex-wrap gap-1.5">
            {state.context_tags.map((tag: string) => (
              <Badge key={tag} variant="outline" className="text-[10px]">{tag}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Multiplayer info */}
      {adventure?.is_multiplayer && (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
            <Users className="w-3 h-3" />{t.play_party} ({players.length})
          </div>
          <PlayerList
            players={players}
            currentUserId={currentUserId}
            currentPlayerId={adventure.current_player_id}
            turnMode={adventure.turn_mode}
            t={t}
          />
          {isHost && adventure.invite_code && <InviteCodeCard inviteCode={adventure.invite_code} t={t} />}
        </div>
      )}

      {/* Quests */}
      <QuestJournal
        adventureId={adventureId}
        isMultiplayer={adventure?.is_multiplayer}
        onWidgetAction={onWidgetAction}
      />

      {/* Discovery Codex */}
      <LoreCodexPanel
        adventure_id={adventureId}
        knownItems={inventoryItems}
        knownNpcs={knownNpcs}
        currentLocation={state?.current_location}
      />

      {/* Player Notes / Scratchpad */}
      <PlayerNotesPanel adventureId={adventureId} />
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: React.ComponentType<{ className?: string }>; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 sm:py-10 gap-2 sm:gap-2.5 text-muted-foreground/40">
      <Icon className="w-8 h-8 sm:w-10 sm:h-10" />
      <p className="text-xs">{text}</p>
    </div>
  );
}

/* ─── Tab definition ─── */
interface HUDTab {
  id: string;
  icon: React.ComponentType<any>;
  label: string;
  ping?: boolean;
  render: () => React.ReactNode;
}

/* ─── Main HUD Component ─── */
// P-30: React.memo prevents GameHUD re-renders when hudProps is stable (memoized in Play.tsx)
export const GameHUD = React.memo(function GameHUD(props: GameHUDProps) {
  const { t } = useLocale();
  const { isAdmin } = useUserRole();
  const {
    adventure, players, currentUserId, isHost, activeMechanics, variables, state,
    playerChar, adventureId, myPlayer, otherUsers,
    combatants = [], onCombatAction, isSending, canSend,
    allowMediaGeneration = false, onWidgetAction,
    mobile = false, characterThought, isGeneratingThought,
    entityImageMap, onJournalEntriesChange,
  } = props;

  const context = useMemo(() => ({
    state: state || {},
    variables: variables || {},
    playerChar: playerChar || {},
  }), [state, variables, playerChar]);

  const widgetLayout = useMemo(
    () => buildWidgetLayout(activeMechanics, context),
    [activeMechanics, context],
  );

  const tabs = useMemo(() => {
    const tabList: HUDTab[] = [];

    const hasInnerVoice = activeMechanics.some((m) => m.id === "inner_voice");

    tabList.push({
      id: "character",
      icon: User,
      label: t.play_character,
      render: () =>
        playerChar?.name
          ? (
            <>
              <CharacterSheet playerChar={playerChar} worldId={adventure?.world_id} adventureId={adventureId} allowMediaGeneration={allowMediaGeneration} entityImageMap={entityImageMap} activeMechanicIds={new Set(activeMechanics.map(m => m.id))} />
              {activeMechanics.some(m => m.id === "inner_voice") && (
                <CharacterThoughts
                  thought={characterThought || null}
                  isGenerating={isGeneratingThought}
                  characterName={playerChar?.name}
                  adventureId={adventureId}
                />
              )}
              <PlayerCommitments commitments={variables?.player_commitments || []} />
            </>
          )
          : <EmptyState icon={User} text={t.play_no_character} />,
    });

    for (const resolved of widgetLayout.sidebar_tabs) {
      const Icon = getIcon(resolved.widget.icon);
      const hasData = Object.values(resolved.data).some((v) =>
        v != null && (Array.isArray(v) ? v.length > 0 : typeof v === "object" ? Object.keys(v).length > 0 : true),
      );

      if (resolved.widget.component === "ReputationWidget") {
        const vars = variables || {};
        const hasFactions = Object.keys(vars).some(k => k.startsWith("reputation_"));
        const hasNPCs = Object.keys(vars._npc_suspicion || {}).length > 0;
        if (!hasFactions && !hasNPCs) continue;
      }

      tabList.push({
        id: resolved.widget.id,
        icon: Icon,
        label: resolved.widget.label,
        ping: hasData,
        render: () => {
          const extraProps: Record<string, any> = {};
          if (resolved.widget.component === "InventoryPanel" && entityImageMap) {
            extraProps.entityImageMap = entityImageMap;
          }
          if (resolved.widget.component === "CombatPanel") {
            extraProps.onAction = onCombatAction || (() => {});
            extraProps.disabled = !!(isSending || !canSend);
            extraProps.currentTurnPlayer = variables?.current_turn_player;
            const dsMap: Record<string, any> = {};
            for (const [k, v] of Object.entries(variables || {})) {
              if (k.startsWith("_death_saves_") && v && typeof v === "object") {
                dsMap[k.replace("_death_saves_", "")] = v;
              }
            }
            if (Object.keys(dsMap).length > 0) extraProps.deathSaves = dsMap;
          }
          if (resolved.widget.component === "QuestJournal") {
            extraProps.adventureId = adventureId;
            extraProps.playerId = myPlayer?.id;
            extraProps.isMultiplayer = adventure?.is_multiplayer;
          }
          if (resolved.widget.component === "LoreCodexPanel") {
            extraProps.adventure_id = adventureId;
            // Gather known items from all inventory scopes
            const knownItems: any[] = [];
            const addItems = (arr: any[], scope: string) => {
              if (!Array.isArray(arr)) return;
              for (const it of arr) {
                const item = typeof it === "string" ? { name: it } : it;
                if (item?.name) knownItems.push({ ...item, scope });
              }
            };
            addItems(playerChar?.inventory || [], "player");
            addItems(variables?.party_inventory || [], "party");
            addItems(variables?.group_inventory || variables?.org_inventory || [], "group");
            addItems(variables?.stash_inventory || [], "stash");
            extraProps.knownItems = knownItems;
            // Known NPCs from active_npcs
            const activeNpcs = variables?.active_npcs || state?.active_npcs || [];
            extraProps.knownNpcs = Array.isArray(activeNpcs)
              ? activeNpcs.map((n: any) => typeof n === "string" ? { name: n } : n)
              : [];
            // Current location
            extraProps.currentLocation = variables?.current_location || state?.current_location;
          }
          if (resolved.widget.component === "ReputationWidget") {
            extraProps.variables = variables;
          }
          if (resolved.widget.component === "WorldMapWidget") {
            extraProps.worldId = adventure?.world_id;
            extraProps.worldConfig = adventure?.world_config || (adventure as any)?.world?.config;
            extraProps.fogOfWar = resolved.data.fogOfWar;
            extraProps.activeMechanicIds = new Set(activeMechanics.map(m => m.id));
            const trackedAssets: Array<{ name: string; location: string; icon: string; color: string }> = [];
            const vars = state?.variables || {};
            if (Array.isArray(vars._tracked_assets)) {
              for (const a of vars._tracked_assets) {
                if (a.name && a.location) trackedAssets.push({
                  name: a.name, location: a.location,
                  icon: a.icon || "default", color: a.color || "hsl(var(--primary))",
                });
              }
            }
            for (const [key, value] of Object.entries(vars)) {
              if (key.endsWith("_location") && typeof value === "string" && !key.startsWith("_")) {
                const assetName = key.replace(/_location$/, "").replace(/_/g, " ");
                if (!trackedAssets.some(a => a.name === assetName)) {
                  trackedAssets.push({
                    name: assetName, location: value,
                    icon: key.includes("ship") ? "ship" : key.includes("car") || key.includes("vehicle") ? "car" : key.includes("drone") ? "drone" : "default",
                    color: "hsl(210 80% 60%)",
                  });
                }
              }
            }
            if (trackedAssets.length > 0) extraProps.trackedAssets = trackedAssets;
          }
          const rendered = renderWidget(resolved, extraProps, onWidgetAction);
          return rendered || <EmptyState icon={Icon} text={tFormat(t.play_no_data, { label: resolved.widget.label.toLowerCase() })} />;
        },
      });
    }

    if (adventure?.is_multiplayer && players.length > 1) {
      tabList.push({
        id: "party",
        icon: Users,
        label: t.play_party,
        ping: true,
        render: () => (
          <PartyPanel
            players={players}
            currentUserId={currentUserId}
            currentPlayerId={adventure?.current_player_id}
            turnMode={adventure?.turn_mode || "free"}
          />
        ),
      });
    }
    tabList.push({
      id: "world",
      icon: Compass,
      label: t.play_world,
      render: () => (
        <WorldTab
          state={state}
          variables={variables}
          adventure={adventure}
          players={players}
          currentUserId={currentUserId}
          isHost={isHost}
          t={t}
          adventureId={adventureId}
          activeMechanics={activeMechanics}
          onWidgetAction={onWidgetAction}
        />
      ),
    });

    const journalEntries: JournalEntry[] = variables?.journal_entries || [];
    tabList.push({
      id: "journal",
      icon: BookOpen,
      label: t.play_journal,
      ping: journalEntries.length > 0,
      render: () => (
        <JournalPanel
          adventureId={adventureId}
          worldId={adventure?.world_id}
          characterName={playerChar?.name}
          turn={state?.turn_count || 0}
          entries={journalEntries}
          onEntriesChange={onJournalEntriesChange || (() => {})}
        />
      ),
    });

    if (isAdmin) {
      tabList.push({
        id: "debug",
        icon: Clapperboard,
        label: t.play_director,
        render: () => (
          <DirectorDebugPanel state={state} variables={variables || {}} />
        ),
      });
    }

    return tabList;
  }, [widgetLayout, playerChar, state, variables, adventure, players, currentUserId, isHost, onCombatAction, isSending, canSend, adventureId, onJournalEntriesChange, isAdmin, t]);

  const [activeTab, setActiveTab] = useState("character");
  const [railExpanded, setRailExpanded] = useState(false);
  const validTab = tabs.find((tab) => tab.id === activeTab) ? activeTab : tabs[0]?.id || "character";
  const activeTabDef = tabs.find((tab) => tab.id === validTab);

  /* ─── Mobile layout ─── */
  if (mobile) {
    return (
      <div {...storyElement("game-hud")} className="flex flex-col h-full overflow-hidden">
        <StatusStrip state={state} variables={variables} statusItems={widgetLayout.status_bar_items} mobile />
        <div className="px-4 py-2 border-b border-border/40 bg-card/20">
          <h3 className="text-sm font-semibold text-foreground tracking-tight">
            {activeTabDef?.label}
          </h3>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-3 sm:p-4">
            {activeTabDef?.render()}
          </div>
        </ScrollArea>
        {adventure?.is_multiplayer && adventureId && (
          <MultiplayerPanel
            adventureId={adventureId}
            currentUserId={currentUserId}
            senderName={myPlayer?.character_name || myPlayer?.profile?.full_name || t.play_anonymous}
            isHost={isHost}
            players={players}
          />
        )}
        <div className="border-t border-border/50 bg-card/50 px-1 py-1.5 flex items-center justify-around gap-0.5 safe-area-bottom">
          {tabs.map((tab) => {
            const isActive = validTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "relative flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-lg transition-all min-w-[48px] min-h-[44px]",
                  isActive
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground active:bg-muted/30",
                )}
              >
                <Icon className="w-4.5 h-4.5" />
                <span className="text-[9px] font-medium leading-none truncate max-w-[56px]">{tab.label}</span>
                {tab.ping && !isActive && (
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  /* ─── Desktop layout ─── */
  return (
    <div {...storyElement("game-hud")} className="flex h-full overflow-hidden">
      <TooltipProvider delayDuration={200}>
        <div className={cn(
          "flex flex-col items-stretch shrink-0 border-r border-border/50 bg-muted/10 py-2 gap-0.5 transition-all duration-200",
          railExpanded ? "w-36" : "w-12",
        )}>
          {tabs.map((tab) => {
            const isActive = validTab === tab.id;
            const Icon = tab.icon;
            const btn = (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "relative flex items-center gap-2.5 rounded-lg transition-all mx-1.5",
                  railExpanded ? "px-2.5 py-2 justify-start" : "justify-center w-9 h-9 mx-auto",
                  isActive
                    ? "bg-primary/15 text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {railExpanded && (
                  <span className="text-xs font-medium truncate">{tab.label}</span>
                )}
                {tab.ping && !isActive && (
                  <span className={cn("absolute w-1.5 h-1.5 rounded-full bg-primary animate-pulse", railExpanded ? "top-1.5 right-1.5" : "top-1 right-1")} />
                )}
              </button>
            );
            return railExpanded ? btn : (
              <Tooltip key={tab.id}>
                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                <TooltipContent side="left" className="text-xs font-medium">{tab.label}</TooltipContent>
              </Tooltip>
            );
          })}

          <button
            onClick={() => setRailExpanded((p) => !p)}
            className="mt-auto mx-auto flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/30 transition-colors"
          >
            {railExpanded ? <ChevronsLeft className="w-3.5 h-3.5" /> : <ChevronsRight className="w-3.5 h-3.5" />}
          </button>

          {otherUsers && otherUsers.length > 0 && (
            <div className="pt-2 border-t border-border/30 px-1">
              <PresenceAvatars users={otherUsers} size="sm" />
            </div>
          )}
        </div>
      </TooltipProvider>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <StatusStrip state={state} variables={variables} statusItems={widgetLayout.status_bar_items} />
        <div className="px-4 py-2.5 border-b border-border/40 bg-card/20">
          <h3 className="text-sm font-semibold text-foreground tracking-tight">
            {activeTabDef?.label}
          </h3>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-4">
            {activeTabDef?.render()}
          </div>
        </ScrollArea>
        {adventure?.is_multiplayer && adventureId && (
          <MultiplayerPanel
            adventureId={adventureId}
            currentUserId={currentUserId}
            senderName={myPlayer?.character_name || myPlayer?.profile?.full_name || t.play_anonymous}
            isHost={isHost}
            players={players}
          />
        )}
      </div>
    </div>
  );
});
